import React, { useRef, useState } from 'react';
import { Download, Loader2, Calendar, MapPin, Phone } from 'lucide-react';
import { toPng } from 'html-to-image';
import { Button } from '../ui/button';
import GirlsFlagQR from './GirlsFlagQR';
import { teamInfo, offSeasonTraining, registrationPricing } from '../../mock';

// 9:16 (1080x1920) Instagram Story flyer for Girls Flag Football.
// Rendered at 540x960 preview; downloaded at 2x pixelRatio -> 1080x1920 PNG.
// Kept as inline styles (no external CSS) so html-to-image renders faithfully.

const FLYER_WIDTH_PX = 540;
const FLYER_HEIGHT_PX = 960;

const WRAPPER_STYLE = {
  width: `${FLYER_WIDTH_PX}px`,
  height: `${FLYER_HEIGHT_PX}px`,
  maxWidth: '100%',
  margin: '0 auto',
  position: 'relative',
  borderRadius: '18px',
  overflow: 'hidden',
  background:
    'radial-gradient(circle at 12% 8%, rgba(255, 215, 0, 0.32) 0%, transparent 42%), ' +
    'radial-gradient(circle at 88% 92%, rgba(255, 100, 200, 0.22) 0%, transparent 40%), ' +
    'linear-gradient(160deg, #050505 0%, #150a10 55%, #050505 100%)',
  border: '2px solid rgba(255, 215, 0, 0.55)',
  boxShadow: '0 30px 90px rgba(255, 215, 0, 0.25)',
  color: '#fff',
  padding: '38px 32px',
  display: 'flex',
  flexDirection: 'column',
  gap: '18px',
  fontFamily: 'system-ui, -apple-system, "Segoe UI", sans-serif',
};

const KICKER_STYLE = {
  color: '#FFD700',
  fontSize: '11px',
  letterSpacing: '5px',
  fontWeight: 800,
  textTransform: 'uppercase',
  textAlign: 'center',
};

const HEADLINE_STYLE = {
  fontSize: '58px',
  fontWeight: 900,
  letterSpacing: '2px',
  color: '#FFD700',
  lineHeight: 0.94,
  margin: 0,
  textAlign: 'center',
  textShadow: '0 4px 32px rgba(255, 215, 0, 0.45)',
  fontStyle: 'italic',
  transform: 'skewX(-4deg)',
};

const HEADLINE_ROW_2_STYLE = {
  ...HEADLINE_STYLE,
  color: '#fff',
  fontSize: '38px',
  textShadow: '0 3px 20px rgba(0,0,0,0.6)',
};

const AGES_PILL_STYLE = {
  alignSelf: 'center',
  background: 'linear-gradient(90deg, #FFD700 0%, #FFC500 100%)',
  color: '#000',
  padding: '8px 22px',
  borderRadius: '999px',
  fontSize: '15px',
  fontWeight: 900,
  letterSpacing: '2.5px',
  boxShadow: '0 6px 18px rgba(255, 215, 0, 0.45)',
  textTransform: 'uppercase',
};

const HOOK_STYLE = {
  fontSize: '17px',
  lineHeight: 1.35,
  fontWeight: 500,
  textAlign: 'center',
  color: '#f5f5f5',
  margin: '4px 0 0 0',
  padding: '0 4px',
};

const BULLET_BOX_STYLE = {
  background: 'rgba(0,0,0,0.4)',
  border: '1px solid rgba(255, 215, 0, 0.35)',
  borderRadius: '14px',
  padding: '16px 20px',
  display: 'flex',
  flexDirection: 'column',
  gap: '10px',
};

const BULLET_STYLE = {
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
  fontSize: '15px',
  fontWeight: 600,
  color: '#fff',
};

const BULLET_DOT_STYLE = {
  width: '8px',
  height: '8px',
  minWidth: '8px',
  borderRadius: '50%',
  background: '#FFD700',
  boxShadow: '0 0 8px rgba(255, 215, 0, 0.8)',
};

const INFO_ROW_STYLE = {
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
  padding: '14px 16px',
  background: 'rgba(255, 215, 0, 0.08)',
  border: '1px solid rgba(255, 215, 0, 0.3)',
  borderRadius: '12px',
};

const INFO_LINE_STYLE = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '8px',
  fontSize: '14px',
  fontWeight: 600,
  color: '#fff',
};

const QR_ROW_STYLE = {
  display: 'flex',
  alignItems: 'center',
  gap: '14px',
  justifyContent: 'space-between',
  marginTop: 'auto',
};

const CTA_STYLE = {
  flex: 1,
  background: 'linear-gradient(90deg, #FFD700 0%, #FFC500 50%, #FFD700 100%)',
  color: '#000',
  padding: '14px 12px',
  borderRadius: '12px',
  fontWeight: 900,
  fontSize: '13px',
  letterSpacing: '2px',
  textAlign: 'center',
  textTransform: 'uppercase',
  boxShadow: '0 6px 20px rgba(255, 215, 0, 0.4)',
};

const HANDLE_STYLE = {
  textAlign: 'center',
  color: '#FFD700',
  fontSize: '13px',
  fontWeight: 700,
  letterSpacing: '1px',
};

const GirlsFlagStoryFlyer = () => {
  const flyerRef = useRef(null);
  const [downloading, setDownloading] = useState(false);

  const price = registrationPricing.girlsFlag?.[0]?.price ?? 140;

  const handleDownload = async () => {
    if (!flyerRef.current) return;
    setDownloading(true);
    try {
      const dataUrl = await toPng(flyerRef.current, {
        cacheBust: true,
        pixelRatio: 2, // 540x960 -> 1080x1920 story-ready PNG
        backgroundColor: '#050505',
        width: FLYER_WIDTH_PX,
        height: FLYER_HEIGHT_PX,
      });
      const link = document.createElement('a');
      link.download = 'Panthers-Girls-Flag-Football-Story.png';
      link.href = dataUrl;
      link.click();
    } catch {
      /* html-to-image can fail on cross-origin webfonts — silent */
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div>
      <div ref={flyerRef} data-testid="girls-flag-story-flyer" style={WRAPPER_STYLE}>
        <div style={KICKER_STYLE}>Randallstown Panthers Present</div>

        <div>
          <h1 style={HEADLINE_STYLE}>GIRLS FLAG</h1>
          <h2 style={HEADLINE_ROW_2_STYLE}>FOOTBALL</h2>
        </div>

        <div style={AGES_PILL_STYLE}>Ages 12 – 14 · Girls Only</div>

        <p style={HOOK_STYLE}>
          Fast-paced, non-contact flag football built specifically for young
          women. Team-first. Skill-focused. Empowering the next generation.
        </p>

        <div style={BULLET_BOX_STYLE}>
          {[
            'Girls-Only Roster · Compete + Belong',
            'No Contact · Skill & Speed Focused',
            'Speed, Agility & Route Development',
            'College-Track Coaching · $' + price + ' Season',
          ].map((line) => (
            <span key={line} style={BULLET_STYLE}>
              <span style={BULLET_DOT_STYLE} />
              {line}
            </span>
          ))}
        </div>

        <div style={INFO_ROW_STYLE}>
          <span style={INFO_LINE_STYLE}>
            <Calendar size={15} color="#FFD700" />
            <span>{offSeasonTraining.schedule.days} · {offSeasonTraining.schedule.time}</span>
          </span>
          <span style={INFO_LINE_STYLE}>
            <MapPin size={15} color="#FFD700" />
            <span>{offSeasonTraining.location.name}</span>
          </span>
          <span style={INFO_LINE_STYLE}>
            <Phone size={15} color="#FFD700" />
            <span>Coach Kenny · {teamInfo.phone.coach_kenny}</span>
          </span>
        </div>

        <div style={QR_ROW_STYLE}>
          <div style={CTA_STYLE}>
            Scan · Register<br />or Reply DM
          </div>
          <GirlsFlagQR size={110} caption="SCAN TO JOIN" />
        </div>

        <div style={HANDLE_STYLE}>@randallstownpanthersfootball</div>
      </div>

      {/* Download button (outside captured area) */}
      <div style={{ textAlign: 'center', marginTop: '1.5rem' }}>
        <Button
          className="btn-primary"
          onClick={handleDownload}
          disabled={downloading}
          data-testid="download-girls-flag-flyer-btn"
          style={{ fontWeight: 700 }}
        >
          {downloading ? (
            <>
              <Loader2 size={18} className="animate-spin" />
              &nbsp;Preparing PNG...
            </>
          ) : (
            <>
              <Download size={18} />
              &nbsp;Download Story Flyer (1080×1920)
            </>
          )}
        </Button>
      </div>
    </div>
  );
};

export default GirlsFlagStoryFlyer;
